<template>
  <div class="stock-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <!-- Executive Toast Notification -->
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Action Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Page Title & Identity -->
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-package-variant-closed</v-icon>
          </div>
          <div>
            <div class="page-title">STOCKS &amp; INVENTORY</div>
            <div class="page-subtitle">Live inventory stock balance, category weights, design counts &amp; supplier batches</div>
          </div>
        </div>

        <!-- Right: Action Bar (Filters + PDF Export) -->
        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <!-- Quick Search -->
          <div class="search-input-wrap">
            <v-text-field
              v-model="keyword"
              clearable
              background-color="white"
              color="#9a6b27"
              hide-details
              placeholder="Search design, item, category..."
              outlined
              dense
              prepend-inner-icon="mdi-magnify"
              class="search-input"
            ></v-text-field>
          </div>

          <!-- Supplier Filter -->
          <div class="filter-dropdown-wrap">
            <v-autocomplete
              v-model="sName"
              :items="supplierList"
              item-text="supplierName"
              item-value="_id"
              placeholder="All Suppliers"
              outlined
              dense
              clearable
              hide-details
              color="#9a6b27"
              class="filter-select"
              prepend-inner-icon="mdi-truck-outline"
            ></v-autocomplete>
          </div>

          <!-- Design Filter -->
          <div class="filter-dropdown-wrap">
            <v-autocomplete
              v-model="designId"
              :items="designList"
              :item-text="(item) => (item && typeof item === 'object' ? (item.name || item.description || '') : (item || ''))"
              item-value="_id"
              placeholder="All Designs"
              outlined
              dense
              clearable
              hide-details
              color="#9a6b27"
              class="filter-select"
              prepend-inner-icon="mdi-shape-outline"
            ></v-autocomplete>
          </div>

          <!-- Refresh Button -->
          <v-btn
            icon
            small
            title="Refresh Stock List"
            class="refresh-icon-btn"
            @click="page = 1; stocklist()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>

          <!-- Download PDF Button -->
          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1 font-weight-bold"
            @click="downloadPDF()"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- Total Units in Stock -->
      <div class="kpi-card" @click="stockFilter = 'All'" :class="{ 'kpi-card-active': stockFilter === 'All' }">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL STOCK PIECES</div>
          <div class="kpi-value">{{ formatNumber(totalItemsQuantity) }} <span class="kpi-unit">pcs</span></div>
          <div class="kpi-hint">Individual units across all items</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-tag-multiple-outline</v-icon>
        </div>
      </div>

      <!-- Cumulative Net Weight -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL NET WEIGHT</div>
          <div class="kpi-value">{{ formatWeight(totalNetWeightSum) }} <span class="kpi-unit">gm</span></div>
          <div class="kpi-hint">Physical inventory gold &amp; metal</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fefce8; color: #ca8a04;">
          <v-icon size="20" color="#ca8a04">mdi-gold</v-icon>
        </div>
      </div>

      <!-- In Stock Items -->
      <div class="kpi-card" @click="stockFilter = 'InStock'" :class="{ 'kpi-card-active': stockFilter === 'InStock' }">
        <div class="kpi-content">
          <div class="kpi-label">IN-STOCK ENTRIES</div>
          <div class="kpi-value">{{ inStockCount }}</div>
          <div class="kpi-hint">Batches ready for sales &amp; billing</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-check-decagram-outline</v-icon>
        </div>
      </div>

      <!-- Depleted / Out of Stock -->
      <div class="kpi-card" @click="stockFilter = 'OutOfStock'" :class="{ 'kpi-card-active': stockFilter === 'OutOfStock' }">
        <div class="kpi-content">
          <div class="kpi-label">DEPLETED / REORDER</div>
          <div class="kpi-value">{{ outOfStockCount }}</div>
          <div class="kpi-hint">Batches with zero inventory</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fef2f2; color: #dc2626;">
          <v-icon size="20" color="#dc2626">mdi-alert-circle-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Stock Status Filter Tabs -->
    <div class="filter-tab-bar mb-3 d-flex align-center justify-space-between flex-wrap" style="gap: 8px;">
      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': stockFilter === 'All' }"
          @click="stockFilter = 'All'"
        >
          All Items
          <span class="filter-pill-badge">{{ array.length }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': stockFilter === 'InStock' }"
          @click="stockFilter = 'InStock'"
        >
          In Stock
          <span class="filter-pill-badge active-pill-badge">{{ inStockCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': stockFilter === 'LowStock' }"
          @click="stockFilter = 'LowStock'"
        >
          Low Stock (≤5)
          <span class="filter-pill-badge warning-pill-badge">{{ lowStockCount }}</span>
        </button>
        <button
          class="filter-pill"
          :class="{ 'filter-pill-active': stockFilter === 'OutOfStock' }"
          @click="stockFilter = 'OutOfStock'"
        >
          Depleted (0)
          <span class="filter-pill-badge inactive-pill-badge">{{ outOfStockCount }}</span>
        </button>
      </div>

      <div class="table-total-count text-muted" style="font-size: 12.5px;">
        Displaying <span class="font-weight-bold text-slate-800">{{ filteredArray.length }}</span> stock items
      </div>
    </div>

    <!-- Master Stock Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <div v-if="filteredArray && filteredArray.length > 0">
        <div class="table-responsive-wrapper">
          <table class="pro-stock-table">
            <thead>
              <tr>
                <th style="width: 55px;" class="text-center">#</th>
                <th style="width: 140px;" class="text-left">Category</th>
                <th style="min-width: 150px;" class="text-left">Item Name</th>
                <th style="min-width: 220px;" class="text-left">Design Name &amp; Spec</th>
                <th style="width: 140px;" class="text-left">Purchase Date</th>
                <th style="width: 130px;" class="text-center">Stock Count</th>
                <th style="width: 140px;" class="text-right">Net Weight</th>
                <th v-if="currentType == 'false'" style="width: 90px;" class="text-center">Tax Type</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(value, i) in filteredArray" :key="value._id && value._id._id ? value._id._id : i" class="table-row">
                <!-- S.No -->
                <td class="text-center text-muted font-weight-medium">
                  {{ page == 1 ? i + 1 : i + 1 + 20 * (page - 1) }}
                </td>

                <!-- Category -->
                <td>
                  <span :class="getCategoryBadgeClass(getCategoryName(value))">
                    <v-icon x-small :color="getCategoryIconColor(getCategoryName(value))" class="mr-1">
                      {{ getCategoryIcon(getCategoryName(value)) }}
                    </v-icon>
                    {{ getCategoryName(value) }}
                  </span>
                </td>

                <!-- Item Name -->
                <td>
                  <div class="item-name-text">
                    {{ getItemName(value) }}
                  </div>
                </td>

                <!-- Design Name & Specs -->
                <td>
                  <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                    <span class="design-name-text">
                      {{ getDesignName(value) }}
                    </span>
                    <span v-if="value._id && value._id.itemPerWeight" class="weight-spec-tag">
                      {{ value._id.itemPerWeight }} gm/pc
                    </span>
                  </div>
                  <div v-if="value._id && value._id.supplier && value._id.supplier.supplierName" class="supplier-subtext mt-1">
                    <v-icon size="11" color="#94a3b8" class="mr-1">mdi-truck-outline</v-icon>
                    {{ value._id.supplier.supplierName }}
                  </div>
                </td>

                <!-- Purchase Date -->
                <td>
                  <div class="date-cell d-flex align-center">
                    <v-icon size="13" color="#94a3b8" class="mr-1">mdi-calendar-blank-outline</v-icon>
                    <span>{{ formatDate(value.lastPurchaseDate) }}</span>
                  </div>
                </td>

                <!-- Stock Count with Status Pill -->
                <td class="text-center">
                  <span v-if="Number(value.quantity) > 5" class="stock-pill stock-healthy">
                    <span class="stock-dot dot-green"></span>
                    {{ value.quantity }} pcs
                  </span>
                  <span v-else-if="Number(value.quantity) > 0" class="stock-pill stock-low">
                    <span class="stock-dot dot-amber"></span>
                    {{ value.quantity }} left
                  </span>
                  <span v-else class="stock-pill stock-empty">
                    <span class="stock-dot dot-red"></span>
                    0 Depleted
                  </span>
                </td>

                <!-- Net Weight -->
                <td class="text-right">
                  <div class="net-weight-val">
                    {{ formatWeight(value.netWeight) }}
                    <span class="unit-text">gm</span>
                  </div>
                </td>

                <!-- Tax Type (if applicable) -->
                <td v-if="currentType == 'false'" class="text-center">
                  <span v-if="value._id && value._id.taxType === false" class="tax-chip-demo">
                    Demo
                  </span>
                  <span v-else class="tax-chip-gst">
                    GST
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Custom Pagination & Summary Bar -->
        <div class="table-pagination-bar px-4 py-3 d-flex flex-wrap align-center justify-space-between">
          <div class="pagination-info">
            Showing
            <span class="font-weight-bold text-slate-800">{{ showingFrom }}</span>
            to
            <span class="font-weight-bold text-slate-800">{{ showingTo }}</span>
            of
            <span class="font-weight-bold text-slate-800">{{ totalEntriesCount }}</span>
            inventory records
          </div>

          <v-pagination
            v-if="Pagelength > 1"
            v-model="page"
            :length="Pagelength"
            :total-visible="7"
            circle
            color="#9a6b27"
            class="custom-pagination"
          ></v-pagination>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="32" color="#94a3b8">mdi-package-variant-closed-remove</v-icon>
        </div>
        <div class="empty-title">No Stock Records Found</div>
        <div class="empty-subtitle">
          {{ (keyword || sName || designId || stockFilter !== 'All') ? 'No stock items match your active filters or search terms. Try resetting filters.' : 'There are currently no items recorded in your stock inventory.' }}
        </div>
        <div class="mt-4 d-flex justify-center" style="gap: 10px;">
          <v-btn
            v-if="keyword || sName || designId || stockFilter !== 'All'"
            small
            outlined
            color="#0f172a"
            @click="clearAllFilters()"
          >
            <v-icon small left>mdi-filter-remove-outline</v-icon>
            Reset Filters
          </v-btn>
          <v-btn
            small
            dark
            color="#9a6b27"
            to="/PurchaseList"
          >
            <v-icon small left>mdi-cart-plus</v-icon>
            Add Stock Purchase
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "StockPage",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,
      to: null,
      page: 1,
      Pagelength: 1,
      totalLength: 0,
      menu1: false,
      menu2: false,
      supplierList: [],
      currentpage: 1,
      sName: "",
      jewelleryId: localStorage.getItem("id"),
      currentType: localStorage.getItem("GSTstatus"),
      array: [],
      limit: 20,
      username: null,
      name: null,
      address: null,
      phone: null,
      email: null,
      designList: [],
      designId: null,
      from: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      taxtypes: ["GST", "DEMO"],
      taxType: "",
      keyword: "",
      stockFilter: "All",
    };
  },

  watch: {
    from() {
      this.page = 1;
      this.stocklist();
    },
    to() {
      this.page = 1;
      this.stocklist();
    },
    page() {
      this.stocklist();
    },
    sName() {
      this.page = 1;
      this.stocklist();
    },
    designId() {
      this.page = 1;
      this.stocklist();
    },
    taxType() {
      this.page = 1;
      this.stocklist();
    },
  },
  mounted() {
    this.stocklist();
    this.getSupplier();
    this.getDesign();
  },
  computed: {
    filteredArray() {
      if (!this.array) return [];
      let list = this.array;

      // Filter by stock status
      if (this.stockFilter === "InStock") {
        list = list.filter((item) => Number(item.quantity) > 0);
      } else if (this.stockFilter === "LowStock") {
        list = list.filter(
          (item) => Number(item.quantity) > 0 && Number(item.quantity) <= 5
        );
      } else if (this.stockFilter === "OutOfStock") {
        list = list.filter(
          (item) => !item.quantity || Number(item.quantity) === 0
        );
      }

      // Filter by search keyword
      if (this.keyword && this.keyword.trim()) {
        const q = this.keyword.trim().toLowerCase();
        list = list.filter((item) => {
          const cat = this.getCategoryName(item).toLowerCase();
          const itemName = this.getItemName(item).toLowerCase();
          const desName = this.getDesignName(item).toLowerCase();
          const supplier =
            item._id && item._id.supplier && item._id.supplier.supplierName
              ? item._id.supplier.supplierName.toLowerCase()
              : "";
          return (
            cat.includes(q) ||
            itemName.includes(q) ||
            desName.includes(q) ||
            supplier.includes(q)
          );
        });
      }

      return list;
    },
    totalItemsQuantity() {
      if (!this.array || !this.array.length) return 0;
      return this.array.reduce(
        (acc, curr) => acc + (Number(curr.quantity) || 0),
        0
      );
    },
    totalNetWeightSum() {
      if (!this.array || !this.array.length) return 0;
      return this.array.reduce(
        (acc, curr) => acc + (Number(curr.netWeight) || 0),
        0
      );
    },
    inStockCount() {
      if (!this.array) return 0;
      return this.array.filter((item) => Number(item.quantity) > 0).length;
    },
    lowStockCount() {
      if (!this.array) return 0;
      return this.array.filter(
        (item) => Number(item.quantity) > 0 && Number(item.quantity) <= 5
      ).length;
    },
    outOfStockCount() {
      if (!this.array) return 0;
      return this.array.filter(
        (item) => !item.quantity || Number(item.quantity) === 0
      ).length;
    },
    totalEntriesCount() {
      return this.totalLength || (this.array ? this.array.length : 0);
    },
    showingFrom() {
      if (!this.filteredArray || this.filteredArray.length === 0) return 0;
      return (this.page - 1) * 20 + 1;
    },
    showingTo() {
      if (!this.filteredArray || this.filteredArray.length === 0) return 0;
      return (this.page - 1) * 20 + this.filteredArray.length;
    },
  },
  methods: {
    getCategoryName(value) {
      if (value && value._id && value._id.design && value._id.design.categoryId) {
        return value._id.design.categoryId.name || "GOLD";
      }
      return "GOLD";
    },
    getItemName(value) {
      if (value && value._id && value._id.design && value._id.design.itemId) {
        return value._id.design.itemId.name || "—";
      }
      return "—";
    },
    getDesignName(value) {
      if (value && value._id && value._id.design) {
        return value._id.design.name || "—";
      }
      return "—";
    },
    getCategoryBadgeClass(catName) {
      const name = (catName || "").toUpperCase();
      if (name.includes("GOLD")) return "cat-chip cat-gold";
      if (name.includes("SILVER")) return "cat-chip cat-silver";
      if (name.includes("DIAMOND")) return "cat-chip cat-diamond";
      if (name.includes("PLATINUM")) return "cat-chip cat-platinum";
      return "cat-chip cat-default";
    },
    getCategoryIcon(catName) {
      const name = (catName || "").toUpperCase();
      if (name.includes("GOLD")) return "mdi-gold";
      if (name.includes("SILVER")) return "mdi-circle-multiple-outline";
      if (name.includes("DIAMOND")) return "mdi-diamond-stone";
      if (name.includes("PLATINUM")) return "mdi-star-four-points";
      return "mdi-tag-outline";
    },
    getCategoryIconColor(catName) {
      const name = (catName || "").toUpperCase();
      if (name.includes("GOLD")) return "#92400e";
      if (name.includes("SILVER")) return "#475569";
      if (name.includes("DIAMOND")) return "#1e40af";
      if (name.includes("PLATINUM")) return "#334155";
      return "#64748b";
    },
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return "0";
      return Number(val).toLocaleString("en-IN");
    },
    formatWeight(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.000";
      return Number(val).toFixed(3);
    },
    formatDate(item) {
      if (!item) return "—";
      const dt = new Date(item);
      if (isNaN(dt.getTime())) return "—";
      return dt.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    clearAllFilters() {
      this.keyword = "";
      this.sName = "";
      this.designId = null;
      this.stockFilter = "All";
      this.page = 1;
      this.stocklist();
    },
    stocklist() {
      this.appLoading = true;
      axios({
        url: "/stock/getalllist",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          taxType: this.taxType,
          designId: this.designId,
          limit: 20,
          page: this.page,
          supplierId: this.sName,
          jewelleryId: this.jewelleryId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.array = response.data.data || [];
            this.totalLength = response.data.totalLength || this.array.length;
            this.Pagelength = Math.ceil(this.totalLength / 20) || 1;
          } else {
            this.msg = response.data.msg || "Failed to load stocks";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    downloadPDF() {
      this.appLoading = true;
      axios({
        method: "post",
        url: "/download/stockreport",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          taxType: this.taxType,
          designId: this.designId,
          supplierId: this.sName,
          jewelleryId: this.jewelleryId,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "STOCK REPORT.pdf");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getSupplier() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/stock/supplier/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.supplierList = response.data.supplierlist || [];
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    getDesign() {
      this.appLoading = true;
      axios({
        method: "post",
        url: "/design",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          jewelleryId: this.jewelleryId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.designList = response.data.alldesignlist || [];
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap');

.stock-page-wrapper {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1e293b;
}

/* 1. Header Card */
.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #0f172a;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

.search-input-wrap {
  width: 220px;
}

.filter-dropdown-wrap {
  width: 175px;
}

@media (max-width: 960px) {
  .search-input-wrap,
  .filter-dropdown-wrap {
    width: 100%;
  }
}

.search-input :deep(.v-input__slot),
.filter-select :deep(.v-input__slot) {
  border-radius: 8px !important;
  border: 1px solid #cbd5e1 !important;
  min-height: 38px !important;
  font-size: 12.5px !important;
}

.refresh-icon-btn {
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  width: 38px !important;
  height: 38px !important;
}

.btn-primary {
  border-radius: 8px !important;
  font-size: 12.5px !important;
  height: 38px !important;
  box-shadow: 0 2px 6px rgba(154, 106, 44, 0.25) !important;
}

/* 2. 4 KPI Overview Metric Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

@media (max-width: 960px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 550px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
  cursor: pointer;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
  transform: translateY(-1px);
}

.kpi-card-active {
  border-color: #9a6b27 !important;
  background: #fffdf8 !important;
  box-shadow: 0 2px 8px rgba(154, 106, 44, 0.12) !important;
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: #64748b;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  margin: 4px 0 2px 0;
}

.kpi-unit {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* 3. Filter Pill Bar */
.filter-pill {
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-pill:hover {
  background: #f8fafc;
  color: #0f172a;
  border-color: #cbd5e1;
}

.filter-pill-active {
  background: #0f172a !important;
  color: #ffffff !important;
  border-color: #0f172a !important;
}

.filter-pill-badge {
  font-size: 10.5px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
}

.filter-pill-active .filter-pill-badge {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

.active-pill-badge {
  background: #dcfce7;
  color: #16a34a;
}

.warning-pill-badge {
  background: #fef3c7;
  color: #b45309;
}

.inactive-pill-badge {
  background: #fee2e2;
  color: #dc2626;
}

/* 4. Master Stock Table */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.table-responsive-wrapper {
  width: 100%;
  overflow-x: auto;
}

.pro-stock-table {
  width: 100%;
  border-collapse: collapse;
}

.pro-stock-table th {
  background-color: #f8fafc;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.pro-stock-table td {
  font-size: 13px;
  color: #334155;
  padding: 12px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.table-row:hover {
  background-color: #fafbfd;
}

/* Category Chips */
.cat-chip {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
}

.cat-gold {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}

.cat-silver {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.cat-diamond {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
}

.cat-platinum {
  background: #f8fafc;
  color: #334155;
  border: 1px solid #e2e8f0;
}

.cat-default {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* Item & Design Typography */
.item-name-text {
  font-weight: 700;
  color: #0f172a;
  font-size: 13px;
  letter-spacing: -0.1px;
}

.design-name-text {
  font-weight: 600;
  color: #334155;
  font-size: 13px;
}

.weight-spec-tag {
  font-family: monospace;
  font-size: 10.5px;
  font-weight: 700;
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  padding: 1px 5px;
  border-radius: 4px;
}

.supplier-subtext {
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
}

.date-cell {
  font-size: 12.5px;
  color: #475569;
}

/* Stock Count Pills */
.stock-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 14px;
  white-space: nowrap;
}

.stock-healthy {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.stock-low {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}

.stock-empty {
  background: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.stock-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.dot-green {
  background: #16a34a;
}

.dot-amber {
  background: #d97706;
}

.dot-red {
  background: #dc2626;
}

/* Net Weight Cell */
.net-weight-val {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
}

.unit-text {
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  margin-left: 2px;
}

/* Tax Chips */
.tax-chip-gst {
  font-size: 10px;
  font-weight: 600;
  background: #eff6ff;
  color: #2563eb;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #dbeafe;
}

.tax-chip-demo {
  font-size: 10px;
  font-weight: 600;
  background: #f8fafc;
  color: #64748b;
  padding: 2px 7px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

/* Pagination Bar */
.table-pagination-bar {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.pagination-info {
  font-size: 12.5px;
  color: #64748b;
}

.custom-pagination :deep(.v-pagination__item) {
  font-size: 12px !important;
  min-width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
}

.custom-pagination :deep(.v-pagination__navigation) {
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 6px !important;
  width: 28px !important;
  height: 28px !important;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}

.text-muted {
  color: #94a3b8;
}

.text-slate-800 {
  color: #1e293b;
}
</style>
